<template>
  <div v-if="showBulkEmailPanel" class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        Send Email to Tenants
        <button style="position: absolute; right: 5px" @click="closePanel()">
          <i class="fas fa-close"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="left-column" style="width:">
          <div>
            <input style="width: 98.5%" v-model="from" placeholder="From:" />
          </div>
          <div>
            <input
              style="width: 80%"
              v-model="subject"
              placeholder="Subject:"
            />
            <button class="make-campaign" @click="openCampaignPopup($event)">
              Campaign
            </button>
          </div>
          <textarea
            v-model="markdownBody"
            @input="updatePreview"
            placeholder="Email Body (Markdown)"
          ></textarea>
          <textarea
            class="email-footer"
            ref="emailFooter"
            v-model="emailFooter"
            @input="updatePreview"
            @blur="updatePreview"
            placeholder="Footer (Markdown)"
          ></textarea>
          <div class="preview-label">
            Body preview:
            <span class="markdown-help">
              <a
                href="https://www.markdownguide.org/cheat-sheet/"
                target="_blank"
                >Markdown guide</a
              >
              <button
                @click="printPreview"
                class="print-preview-btn"
                title="Print preview"
              >
                <i class="fas fa-print"></i>
              </button>
            </span>
          </div>
          <div v-html="markdownPreview" class="preview"></div>
          <div class="attachment-section">
            <div class="attachment-label">
              Optional Attachment <i class="fas fa-paperclip"></i>
            </div>
            <input type="file" @change="onFileChange" ref="fileInput" />
            <button @click="clearFile" v-if="attachment">Remove</button>
            <span style="font-size: 0.8em" v-if="attachment">{{
              attachment.name
            }}</span>
          </div>
        </div>
        <div class="right-column" style="width: 60%">
          <div class="button-group">
            <button
              style="position: relative; left: -154px"
              @click="selectTemplateButtonClicked"
            >
              Templates
            </button>
            <input
              type="text"
              v-model="searchQuery"
              @input="debouncedFilterEmails"
              placeholder="Search tenants..."
              class="search-input"
            />
            <button @click="clearSearch">Clear</button>
            <button @click="showSendPreview">Send</button>
            <button @click="checkOnlyFiltered">Sel.</button>
            <button @click="closePanel">Close</button>
          </div>
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th class="included" @click="toggleAll">
                    {{ allSelected ? 'Deselect' : 'Select' }}
                  </th>
                  <th>Address</th>
                  <th>Tenant</th>
                  <th>Emails</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(email, index) in filteredEmails"
                  :key="`${email.email}-${index}`"
                  :class="{ even: index % 2 === 0, odd: index % 2 !== 0 }"
                >
                  <td class="included">
                    <input type="checkbox" v-model="email.included" />
                  </td>
                  <td v-html="highlightMatch(email.address)"></td>
                  <td
                    v-html="highlightMatch(email.fullName)"
                    class="ellipsis"
                  ></td>
                  <td
                    v-html="highlightMatch(email.email)"
                    class="ellipsis"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            style="margin-left: 5px; margin-top: 10px; font-size: 0.9em"
            v-if="filteredEmails.length < 10"
            @click="getEmailAddresses"
          >
            To expand the list to all tenants,
            <span
              style="
                cursor: pointer;
                color: #a008a0;
                text-decoration: underline;
              "
              >click</span
            >
            to expand
          </div>
          <div
            style="margin-left: 5px; margin-top: 10px; font-size: 0.9em"
            v-if="filteredEmails.length === 0"
            @click="getEmailAddresses"
          >
            Please wait while fetching emails, or
            <span
              style="
                cursor: pointer;
                color: #a008a0;
                text-decoration: underline;
              "
              >click</span
            >
            to retry
          </div>
        </div>
      </div>
    </div>

    <SendPreview
      v-if="showPreview"
      :from="from"
      :subject="subject"
      :body="markdownPreview"
      :to="previewTo"
      @closePreview="closePreview"
      :attachmentName="attachment ? attachment.name : ''"
      @sendAll="sendEmails"
    />

    <!-- Popup Window for Email Templates -->
    <div v-if="showPopup" class="popup-window">
      <div style="margin-left: -1px" class="modal-header">Use Templates</div>
      <div style="height: 480px">
        <div class="popup-left-column">
          <label>Select template</label>
          <div class="template-list">
            <div
              v-for="template in templates"
              :title="'double click\nto edit'"
              :key="template.id"
              @click="selectTemplate(template)"
              @dblclick="openEditModal(template)"
              :class="[
                'template-card',
                {
                  selected:
                    selectedTemplate && selectedTemplate.id === template.id,
                },
              ]"
            >
              <div class="template-name">{{ template.name }}</div>
              <div class="template-desc">{{ template.description }}</div>
            </div>
          </div>
        </div>
        <div class="popup-right-column">
          <label>Template preview <small> (double-click to edit)</small></label>
          <div
            @dblclick="openEditModal(this.selectedTemplate)"
            class="preview-markdown"
            v-html="selectedMarkdown"
          ></div>
        </div>
      </div>
      <div class="button-row">
        <button @click="useTemplate">Use</button>
        <button @click="newTemplate">New</button>
        <button @click="deleteTemplate(this.selectedTemplate.id)">Del</button>
        <button @click="closePopup">Close</button>
      </div>
    </div>

    <!-- Modal for Editing/Creating Template -->
    <div v-if="showEditModal" class="edit-template-modal">
      <div class="modal-header">
        {{ isEditing ? 'Edit Template' : 'New Template' }}
      </div>
      <div class="modal-content">
        <textarea
          ref="newTemplateName"
          style="height: 15px"
          v-model="editableTemplate.name"
          placeholder="Template Name"
        ></textarea>
        <textarea
          style="height: 30px"
          v-model="editableTemplate.description"
          placeholder="Template Description"
        ></textarea>
        <textarea
          style="height: 325px"
          v-model="editableTemplate.markdown"
          placeholder="Template Markdown"
        ></textarea>
        <button @click="saveTemplate">Save</button>
        <button @click="closeEditModal">Cancel</button>
      </div>
    </div>
  </div>
  <div v-if="showCampaignPopup" class="campaign-popup" ref="campaignPopup">
    <div class="campaign-popup-header">Campaign Settings</div>
    <input
      class="campaign-title"
      v-model="campaignTitle"
      placeholder="campaign title"
    />
    <div class="campaign-action-buttons">
      <small>Expected response: </small>
      <button
        @click="campaignMode = 'accept'"
        :class="{ activeCampaignMode: campaignMode === 'accept' }"
      >
        Accept
      </button>

      <button
        @click="campaignMode = 'decline'"
        :class="{ activeCampaignMode: campaignMode === 'decline' }"
      >
        Decline
      </button>

      <button
        @click="campaignMode = 'both'"
        :class="{ activeCampaignMode: campaignMode === 'both' }"
      >
        Both
      </button>
    </div>
    <div class="campaign-popup-body">
      <textarea v-model="campaignFooter" class="campaign-message"></textarea>
      <div style="margin-top: 5px; margin-bottom: 5px; font-size: 0.8em">
        Preview:
      </div>
      <div class="campaign-preview" v-html="campaignMarkdown"></div>
      <div
        style="margin-top: 8px; margin-left: -4px"
        class="campaign-action-buttons"
      >
        <button>Create</button>
        <button>Update</button>
        <button @click="showCampaignPopup = false">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import { marked } from 'marked';
import SendPreview from './SendPreview.vue';
import debounce from 'lodash/debounce';

// Define Pixie Properties as a constant entry
const PIXIE_PROPERTIES_ENTRY = {
  fullName: 'Eric',
  address: 'Pixie Properties',
  email: 'eric@bigpropertymanagement.com',
  included: true,
};

export default {
  emits: ['close-panel'],
  components: {
    SendPreview,
  },
  props: {
    showBulkEmailPanel: Boolean,
  },
  data() {
    return {
      allEmails: [],
      filteredEmails: [],
      from: 'communications@bigpropertymanagement.com',
      subject: '',
      markdownBody: 'Dear Tenants, <br><br>',
      markdownPreview: '',
      showPreview: false,
      allSelected: false,
      searchQuery: '',
      attachment: null,
      emailFooter:
        '<br>Regards,<br>**The Pixie Properties Team**<br>616-344-0214 - Text or Voice<br><br><small>If you are no longer a tenant of Pixie Properties please disregard this email and reply so that we can remove you from future mailings.</small>',

      showPopup: false,
      templates: [],
      selectedMarkdown: '',
      selectedTemplate: null,
      showEditModal: false,
      isEditing: false,
      editableTemplate: { name: '', description: '', markdown: '' }, // Template being edited or created
      showCampaignPopup: false,
      campaignId: 0,
      campaignTitle: '',
      campaignFooter: 'Your message',
      campaignMode: 'accept',
    };
  },
  computed: {
    compiledMarkdown() {
      // Set up marked with GFM enabled
      marked.setOptions({
        gfm: true,
        breaks: true, // Optional: Enable line breaks like in GitHub comments
      });

      // Return the compiled HTML from markdown content
      return marked(this.markdownContent);
    },

    campaignMarkdown() {
      return marked(this.campaignFooter);
    },

    sortedEmails() {
      // Display Pixie Properties at the top followed by the rest of filteredEmails
      return [PIXIE_PROPERTIES_ENTRY, ...this.filteredEmails];
    },
  },

  created() {
    this.debouncedFilterEmails = debounce(this.filterEmails, 300);
    this.handleSelectedProperties();
  },

  mounted() {
    this.updatePreview();
    this.attachment = null;
  },

  beforeUnmount() {},

  methods: {
    printPreview() {
      const previewContent = this.markdownPreview;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Email Preview</title>
          <style>
            @page {
              size: portrait;
              margin: 0.75in;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12pt;
              line-height: 1.5;
              color: #333;
              max-width: 100%;
              padding: 20px;
              box-sizing: border-box;
            }
            h1, h2, h3, h4, h5, h6 {
              margin-top: 1em;
              margin-bottom: 0.5em;
              color: #222;
            }
            h1 { font-size: 18pt; }
            h2 { font-size: 16pt; }
            h3 { font-size: 14pt; }
            p {
              margin: 0.5em 0;
            }
            ul, ol {
              margin: 0.5em 0;
              padding-left: 1.5em;
            }
            li {
              margin: 0.25em 0;
            }
            a {
              color: #0066cc;
              text-decoration: underline;
            }
            blockquote {
              margin: 1em 0;
              padding-left: 1em;
              border-left: 3px solid #ccc;
              color: #666;
            }
            code {
              background: #f4f4f4;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: monospace;
            }
            pre {
              background: #f4f4f4;
              padding: 10px;
              border-radius: 5px;
              overflow-x: auto;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 1em 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background: #f4f4f4;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            hr {
              border: none;
              border-top: 1px solid #ddd;
              margin: 1em 0;
            }
            .notice-header {
              text-align: center;
              color: #6e5875;
              font-size: 18pt;
              margin-bottom: 1em;
            }
          </style>
        </head>
        <body>
          <h2 class="notice-header">Notice to Tenants</h2>
          ${previewContent}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    openCampaignPopup(event) {
      // Show the campaign popup
      this.showCampaignPopup = true;

      // Use this.$nextTick to wait for the DOM updates
      this.$nextTick(() => {
        // Get the position of the cursor
        const cursorX = event.clientX;
        const cursorY = event.clientY;

        // Access the popup element using the ref name 'campaignPopup'
        const popupElement = this.$refs.campaignPopup;

        // Position the popup at the cursor location
        if (popupElement) {
          popupElement.style.position = 'absolute';
          popupElement.style.left = `${cursorX - 30}px`;
          popupElement.style.top = `${cursorY + 30}px`;
        }
      });
    },

    shouldDisplay() {
      const nonDisplayRoutes = ['/', '/login', '/home', '/mhome', '/nothing'];
      let currentRoute = this.$route.path;
      return !nonDisplayRoutes.includes(currentRoute);
    },

    async getEmailAddresses() {
      if (this.shouldDisplay()) {
        try {
          // returns rowNum, address, fullName, email
          const response = await axios.get('/api/tenant-property-emails');
          this.allEmails = [
            PIXIE_PROPERTIES_ENTRY,
            ...response.data.map((email) => ({
              ...email,
              included: !email.suppressed,
            })),
          ];

          // Initialize filteredEmails with allEmails to include all data initially
          this.filteredEmails = [...this.allEmails];
        } catch (error) {
          console.error('Error fetching emails:', error);
        }
      }
    },

    handleSelectedProperties() {
      const selectedPropertiesStr = localStorage.getItem('selectedProperties');
      if (selectedPropertiesStr) {
        try {
          const selectedProperties = JSON.parse(selectedPropertiesStr);
          if (selectedProperties && selectedProperties.length > 0) {
            let rowCounter = 1; // Initialize the row counter starting at 1

            this.allEmails = [
              PIXIE_PROPERTIES_ENTRY,
              ...selectedProperties
                .filter((property) => property)
                .flatMap((property) => {
                  const source =
                    typeof property.sendEmails === 'string' &&
                    property.sendEmails.trim()
                      ? property.sendEmails
                      : property.emails;
                  if (!source) return [];
                  const emailAddresses = source.split(',').filter(Boolean);

                  return emailAddresses.map((email) => ({
                    row: rowCounter++, // Assign and increment the row counter
                    address: property.address || 'Unknown Address',
                    fullName: property.tenants || 'Unknown Tenant',
                    email: email.trim(),
                    included: true,
                    bounceCount: 0,
                    suppressed: false,
                  }));
                }),
            ];

            this.filteredEmails = [...this.allEmails];
          } else {
            this.getEmailAddresses(); // If no valid properties, fetch all emails
          }
        } catch (error) {
          console.error(
            'Error parsing selectedProperties from localStorage:',
            error,
          );
          this.getEmailAddresses(); // Fallback to API call
        }
      } else {
        this.getEmailAddresses(); // Fetch from API if localStorage is unavailable
      }
    },

    updatePreview() {
      this.markdownPreview = marked.parse(
        this.markdownBody + '\r\n' + this.emailFooter,
      );
    },

    showSendPreview() {
      this.showPreview = true;
      if (this.filteredEmails.length > 0) {
        this.previewTo = this.filteredEmails[0].fullName;
      }
    },

    closePanel() {
      this.$emit('close-panel');
      this.markdownBody = 'Dear Tenants, <br><br>';
      this.updatePreview();
      this.closePopup();
      this.showCampaignPopup = false;
    },

    closePreview() {
      this.showPreview = false;
    },

    toggleAll() {
      this.allSelected = !this.allSelected;
      this.filteredEmails.forEach(
        (email) => (email.included = this.allSelected),
      );
    },

    clearSearch() {
      this.searchQuery = '';
      this.filteredEmails = [...this.allEmails];
    },

    filterEmails() {
      const query = this.searchQuery.trim().toLowerCase();
      const terms = query
        .split(',')
        .map((term) => term.trim())
        .filter(Boolean);

      if (terms.length === 0) {
        this.filteredEmails = [...this.allEmails];
      } else {
        this.filteredEmails = this.allEmails.filter((email) =>
          terms.every((term) =>
            Object.values(email).some(
              (value) =>
                typeof value === 'string' && value.toLowerCase().includes(term),
            ),
          ),
        );
      }
    },

    checkOnlyFiltered() {
      // Update included status only for filtered results
      this.allEmails.forEach(
        (email) => (email.included = this.filteredEmails.includes(email)),
      );
    },

    highlightMatch(text) {
      if (!this.searchQuery) return text;
      const terms = this.searchQuery
        .split(',')
        .map((term) => term.trim())
        .filter(Boolean);
      let highlightedText = text;
      terms.forEach((term) => {
        const regex = new RegExp(`(${term})`, 'gi');
        highlightedText = highlightedText.replace(
          regex,
          '<span class="highlight">$1</span>',
        );
      });
      return highlightedText;
    },

    // Helper function to add delay
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async sendEmails() {
      this.closePreview();
      // Filter included emails to get only those marked for sending
      const includedEmails = this.filteredEmails.filter(
        (email) => email.included,
      );

      try {
        // Loop through each included email entry
        for (const email of includedEmails) {
          // Split each email entry on ";" to handle multiple recipients per tenant
          const recipients = email.email
            .split(';')
            .map((e) => e.trim())
            .filter(Boolean); // Filter out empty values in case of extra semicolons

          // Loop through each individual recipient
          for (const recipient of recipients) {
            const formData = new FormData();
            formData.append('from', this.from);
            formData.append('recipients', recipient); // Set a single recipient
            formData.append('subject', this.subject);
            formData.append('body', this.markdownPreview);

            if (this.attachment) {
              formData.append('attachment', this.attachment);
            }

            try {
              // Send a request for each recipient
              const response = await axios.post('/api/send-email', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
              console.log(`Email sent successfully to ${recipient}:`, response);
            } catch (error) {
              console.error(`Failed to send email to ${recipient}:`, error);
              // Break out of the loop if an error occurs to allow for resuming later
              break;
            }

            // Add a delay of 2 seconds between each email
            await this.delay(2000);
          }

          // Unset the included flag for the email object after all recipients are processed
          email.included = false;
        }

        console.log('Email sending process completed.');
      } catch (error) {
        console.error('Failed to send emails:', error);
      }

      // Close the email panel after processing all requests
      this.closePanel();
    },

    onFileChange(event) {
      this.attachment = event.target.files[0];
      // console.log('Selected attachment:', this.attachment); // Log to confirm attachment data
    },

    clearFile() {
      this.attachment = null;
      this.$refs.fileInput.value = ''; // Reset the file input
    },

    async selectTemplateButtonClicked() {
      try {
        const response = await axios.get('/api/get-email-templates');
        this.templates = response.data;
        this.showPopup = true;
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    },

    selectTemplate(template) {
      this.selectedTemplate = template;
      this.selectedMarkdown = marked.parse(template.markdown);
    },

    useTemplate() {
      this.markdownBody = this.selectedTemplate.markdown + '\n';
      this.updatePreview();
      this.closePopup();
    },

    closePopup() {
      this.showPopup = false;
      this.selectedMarkdown = '';
    },

    openEditModal(template) {
      this.isEditing = true;
      this.editableTemplate = { ...template }; // Clone the template to prevent direct mutations
      this.showEditModal = true;
    },

    newTemplate() {
      this.isEditing = false;
      this.editableTemplate = { name: '', description: '', markdown: '' };
      this.showEditModal = true;
    },

    async saveTemplate() {
      if (this.isEditing) {
        // Call API to update the template using axios.put
        try {
          const response = await axios.put(
            `/api/update-email-template/${this.editableTemplate.id}`,
            this.editableTemplate,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );

          console.log('Email template updated successfully:', response.data);
          // Optionally refresh the list of templates or update the UI
        } catch (error) {
          if (error.response) {
            console.error(
              'Failed to update email template:',
              error.response.data.error,
            );
          } else {
            console.error(
              'Unexpected error during email template update:',
              error.message,
            );
          }
        }
      } else {
        // Call API to create a new template using axios.post
        try {
          const response = await axios.post(
            '/api/new-email-template',
            this.editableTemplate,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );

          this.editableTemplate.id = response.data.templateId;
        } catch (error) {
          if (error.response) {
            console.error(
              'Failed to create new email template:',
              error.response.data.error,
            );
          } else {
            console.error(
              'Unexpected error during email template creation:',
              error.message,
            );
          }
        }
      }
      this.closeEditModal();
      this.selectTemplateButtonClicked();
      this.selectedTemplate = this.editableTemplate;
      this.selectTemplate(this.selectedTemplate);
    },

    closeEditModal() {
      this.showEditModal = false;
    },

    async deleteTemplate(templateId) {
      try {
        const response = await axios.delete(
          `/api/delete-email-template/${templateId}`,
        );

        if (response.status === 200) {
          console.log('Email template deleted successfully:', response.data);
          this.selectedTemplate = null;
          this.selectedMarkdown = '';
          this.selectTemplateButtonClicked();
        }
      } catch (error) {
        if (error.response) {
          console.error(
            'Failed to delete email template:',
            error.response.data.error,
          );
        } else {
          console.error(
            'Unexpected error during email template deletion:',
            error.message,
          );
        }
      }
    },
  },

  watch: {
    showEditModal(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.newTemplateName) {
            this.$refs.newTemplateName.focus();
          }
        });
      }
    },

    showBulkEmailPanel(newVal) {
      if (!newVal) {
        this.showPreview = false;
      } else {
        this.attachment = null;
        if (this.shouldDisplay()) {
          console.log('shouldDisplay');
          this.handleSelectedProperties();
        }
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10002;
}

.modal {
  background: white;
  width: 1200px;
  max-width: 1200px;
  border-radius: 5px;
  overflow: hidden;
  max-height: 85vh;
  z-index: 10003;
  position: absolute;
  top: 70px;
  box-shadow: 7px 7px 8px 4px rgba(0, 0, 0, 0.2);
  left: 116px;
}

.modal-header {
  background-color: #333;
  color: #eee;
  padding: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.modal-body {
  display: flex;
}

.left-column,
.right-column {
  flex: 1;
  padding: 20px;
}

.right-column {
  min-width: 690px;
  max-width: 690px;
}

.left-column {
  padding: 20px;
  max-width: 465px;
}

.right-column .button-group {
  text-align: right;
  margin-right: 15px;
  margin-bottom: 5px;
}

textarea {
  width: 96%;
  height: 150px;
}

.preview {
  border-radius: 5px;
  margin-top: 10px;
  border: 1px solid #ddd;
  padding: 10px;
  min-height: 250px;
  max-height: 250px;
  overflow-y: auto;
  font-size: 0.75em;
  width: 448px;
}

.print-preview-btn {
  background: transparent;
  border: 1px solid #999;
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 8px;
  cursor: pointer;
  font-size: 0.9em;
  color: #666;
}

.print-preview-btn:hover {
  background: #eee;
  color: #333;
}

.table-wrapper {
  overflow-y: auto;
  padding: 4px;
  max-height: 600px;
  overflow-x: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7em;
  font-family: Arial, Helvetica, sans-serif;
}

table .included {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  text-align: center;
}

table thead tr {
  background: #ccc;
}

th,
td {
  border: 1px solid #ddd;
  padding: 2px;
  text-align: left;
  white-space: nowrap;
}

th:nth-child(1) {
  max-width: 10%;
  min-width: 10%;
  text-align: center;
}

th:nth-child(2) {
  max-width: 20%;
  min-width: 20%;
  white-space: nowrap;
}

th:nth-child(3) {
  max-width: 30%;
  min-width: 30%;
}

th:nth-child(4) {
  max-width: 40%;
  min-width: 40%;
}

tr.even {
  background: #f2f2f2;
}

tr.odd {
  background: #ffffff;
}

button {
  margin: 0 5px;
}

td.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

input {
  margin-bottom: 5px;
  padding: 3px;
  border: 1pt solid #ddd;
  border-radius: 3px;
}

.preview-label {
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 5px;
  margin-top: 5px;
  margin-left: 4px;
}

:deep(.highlight) {
  background-color: yellow;
  font-weight: bold;
}

.markdown-help {
  margin-top: 8px;
  margin-left: 8px;
  font-size: 0.8em;
}

.attachment-section {
  margin-top: 10px;
}

.attachment-section input {
  margin-bottom: 5px;
  padding: 3px;
  font-size: 0.8em;
  border: 1pt solid #ddd;
  border-radius: 3px;
  width: 99%;
}

.attachment-label {
  margin-top: 4px;
  margin-bottom: 4px;
  margin-left: 3px;
  font-size: 0.7em;
}

.email-footer {
  height: 40px;
  font-size: 0.8em;
  padding-top: 2px;
}

.popup-window {
  width: 676px;
  left: 616px;
  position: absolute;
  top: 242px;
  background: #e3e2e2;
  border-radius: 5px;
  z-index: 10003;
  box-shadow: 7px 7px 8px 4px rgba(0, 0, 0, 0.2);
  border: 1px solid #c1c1c1;
}

.popup-window label {
  font-size: 0.9em;
}

.popup-content {
  background: #fff;
  padding: 8px;
  width: 547px;
  max-width: 600px;
  position: absolute;
  height: 420px;
}

.popup-left-column,
.popup-right-column {
  display: inline-block;
  vertical-align: top;
  overflow-y: auto;
  margin-left: 4px;
  margin-top: 8px;
}
.popup-left-column {
  width: 220px;
}

.popup-right-column {
  width: 437px;
}

.template-list {
  font-size: 0.8em;
  overflow-y: auto;
  height: 400px;
  border-radius: 4px;
  margin-top: 6px;
}

.preview-markdown {
  font-size: 0.7em;
  overflow-y: auto;
  height: 455px;
  padding: 7px;
  border-radius: 4px;
  background: #fff;
  margin-top: 6px;
  user-select: text;
}

.template-card {
  border: 1px solid #ccc;
  padding: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  background: #fff;
  border-radius: 4px;
  width: 82%;
}

.button-row {
  margin-bottom: 10px;
}

.button-row button {
  width: 45px;
}

button {
  margin: 0 5px;
}

.template-name {
  font-size: 0.9em;
  font-weight: bold;
}
.template-desc {
  font-size: 0.8em;
}

.selected {
  border-color: #a008a0;
}

.edit-template-modal {
  background: white;
  width: 677px;
  max-width: 1200px;
  border-radius: 5px;
  overflow: hidden;
  /* max-height: 85vh; */
  z-index: 10003;
  position: absolute;
  top: 242px;
  box-shadow: 7px 7px 8px 4px rgba(0, 0, 0, 0.2);
  left: 616px;
  height: 551px;
}

.edit-template-modal textarea {
  margin: 6px;
  font-size: 0.8em;
  width: 641px;
}

.make-campaign {
  margin-left: 5px;
  border-radius: 5px;
  height: 25px;
  border: 1px solid #aaa;
  font-size: 9pt;
}

.campaign-popup {
  background: #eee;
  width: 400px;
  box-shadow: 7px 7px 8px 4px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  border: 1px solid #aaa;
  padding: 10px;
  font-size: 0.8em;
  z-index: 10004;
  position: absolute;
  left: 100px;
  top: 50px;
}

.campaign-title {
  margin-bottom: 5px;
  padding: 4px;
  border: 1pt solid #ddd;
  border-radius: 5px;
  width: 392px;
}

.campaign-popup-header {
  margin: -10px;
  margin-bottom: 15px;
  background: #333;
  color: #fff;
  padding: 5px;
  font-size: 1.2em;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.campaign-action-buttons {
  margin: 5px;
  margin-left: 0;
}

.campaign-action-buttons button {
  border-radius: 5px;
  border: 1px solid #aaa;
  padding: 5px;
  font-size: 0.9em;
  width: 70px;
}

.campaign-popup-body {
  margin-top: 5px;
}

.campaign-message {
  width: 382px;
  height: 100px;
}

.campaign-preview {
  padding: 4px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 0.7em;
  height: 100px;
  width: 392px;
}
.campaign-button-footer {
  margin-top: 15px;
  margin-bottom: 10px;
}

.activeCampaignMode {
  background: #af99b5;
}
</style>
